import { useState, useEffect, useRef } from 'react';
import { fetchNotifications } from '../api';

const useNotificationPoller = (userId, userType, interval = 10000) => {
    const [latestNotification, setLatestNotification] = useState(null);
    // Use a ref to store the last known latest ID to avoid re-renders if nothing changes
    // But we need to update it when we fetch
    const lastSeenIdRef = useRef(null);
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        if (!userId || userId === 999 || userId === '999') {
            console.log("Poller: Skipping poll for guest/mock user ID (999)");
            return;
        }

        // Also check if token is mock
        const checkMockToken = () => {
             const userData = localStorage.getItem("userLoginData") || localStorage.getItem("hostLoginData") || localStorage.getItem("adminLoginData");
             if (userData) {
                 try {
                     const parsed = JSON.parse(userData);
                     const token = parsed.token || (parsed.data && parsed.data.token);
                     return token && token.startsWith('mock-');
                 } catch (e) { return false; }
             }
             return false;
        };

        if (checkMockToken()) {
            console.log("Poller: Skipping poll for mock token session");
            return;
        }

        const poll = async () => {
            try {
                const data = await fetchNotifications(userId, userType);
                setNotifications(data);

                if (data && data.length > 0) {
                    const newest = data[0];
                    const newestId = Number(newest.id);
                    const currentId = Number(lastSeenIdRef.current);

                    // Initial load
                    if (lastSeenIdRef.current === null) {
                        // Check local storage first to avoid re-popping seen notifications on refresh
                        const storedId = localStorage.getItem(`lastSeenNotificationId_${userId}`);
                        const sessionLastId = storedId ? Number(storedId) : 0;

                        lastSeenIdRef.current = newestId > sessionLastId ? newestId : sessionLastId;

                        // Only notify if it's strictly newer than what we've ever seen
                        // AND it's unread
                        if (newestId > sessionLastId && !newest.isRead) {
                            console.log("Poller: Initial load found new unseen notification:", newestId);
                            setLatestNotification(newest);
                            localStorage.setItem(`lastSeenNotificationId_${userId}`, newestId);
                        } else {
                            console.log("Poller: Initial load. Newest:", newestId, "StorageLast:", sessionLastId, ". No popup.");
                            // If storage has nothing, we mark current newest as seen to avoid popping up old stuff
                            if (!storedId) {
                                localStorage.setItem(`lastSeenNotificationId_${userId}`, newestId);
                            }
                        }
                    }
                    // Subsequent polls
                    else if (newestId > currentId) {
                        console.log("Poller: New notification detected!", newestId);
                        lastSeenIdRef.current = newestId;
                        localStorage.setItem(`lastSeenNotificationId_${userId}`, newestId);

                        if (!newest.isRead) {
                            setLatestNotification(newest);
                        }
                    }
                }
            } catch (error) {
                console.warn("Notification polling failed", error);
            }
        };

        // Initial fetch
        poll();

        const timer = setInterval(poll, 15000); // Poll every 15 seconds (reduced for better performance)
        return () => clearInterval(timer);
    }, [userId, userType, interval]);

    const clearNotification = () => {
        setLatestNotification(null);
    };

    return { latestNotification, clearNotification, notifications };
};

export default useNotificationPoller;
